import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../../../contexts/I18nContext';
import './PromptInput.scss';

const PromptInput = ({
  id,
  title,
  description,
  required = false,
  value,
  onChange,
  onValidationChange,
  disabled = false,
  className = '',
  height = null,
  props = {}
}) => {
  const { t } = useI18n();
  const [localValue, setLocalValue] = useState(value || '');
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const textareaRef = useRef(null);

  // 从props中提取具体属性，如果props中没有则使用默认值
  const {
    placeholder = title ? `${t('aiToolTemplate.promptInput.defaultPlaceholder')}` : t('aiToolTemplate.promptInput.defaultPlaceholder'),
    minLength = 0,
    maxLength = 2000,
    rows = 4,
    suggestions = [],
    showCharCount = true,
    autoResize = true,
    validation = {}
  } = props;

  // 同步外部值变化
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value || '');
    }
  }, [value, localValue]);

  // 验证输入
  const validateInput = (inputValue) => {
    let valid = true;
    let message = '';

    // 必填验证
    if (required && !inputValue.trim()) {
      valid = false;
      message = t('aiToolTemplate.promptInput.validation.required');
    }
    // 最小长度验证
    else if (minLength > 0 && inputValue.trim().length < minLength) {
      valid = false;
      message = t('aiToolTemplate.promptInput.validation.minLength', { min: minLength });
    }
    // 最大长度验证
    else if (maxLength > 0 && inputValue.length > maxLength) {
      valid = false;
      message = t('aiToolTemplate.promptInput.validation.maxLength', { max: maxLength });
    }
    // 自定义验证
    else if (validation && validation.pattern && !new RegExp(validation.pattern).test(inputValue)) {
      valid = false;
      message = validation.message || t('aiToolTemplate.promptInput.validation.pattern');
    }

    setIsValid(valid);
    setValidationMessage(message);
    
    // 通知父组件验证状态
    if (onValidationChange) {
      onValidationChange(valid, message);
    }

    return valid;
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // 验证输入
    validateInput(newValue);
    
    // 通知父组件
    if (onChange) {
      onChange(newValue);
    }

    // 自动调整高度
    if (autoResize && textareaRef.current && !height) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // 处理建议点击
  const handleSuggestionClick = (suggestion) => {
    const newValue = suggestion.content || suggestion;
    setLocalValue(newValue);
    validateInput(newValue);
    setShowSuggestions(false);
    
    if (onChange) {
      onChange(newValue);
    }

    // 聚焦到文本框
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // 处理焦点事件
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // 延迟隐藏建议，以便点击建议时能正常触发
    setTimeout(() => setShowSuggestions(false), 200);
  };

  // 处理键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && suggestions.length > 0) {
      e.preventDefault();
      setShowSuggestions(!showSuggestions);
    }
  };

  // 计算字符数
  const charCount = localValue.length;
  const isOverLimit = maxLength > 0 && charCount > maxLength;

  return (
    <div className={`prompt-input-component ${className}`}>
      {/* 组件标题和描述 */}
      <div className="component-header">
        <h3 className="component-title">
          {title}
          {required && <span className="required-mark">*</span>}
        </h3>
        {description && (
          <p className="component-description">{description}</p>
        )}
      </div>

      {/* 输入区域 */}
      <div className="input-container">
        <div className={`textarea-wrapper ${isFocused ? 'focused' : ''} ${!isValid ? 'error' : ''}`}>
          <textarea
            ref={textareaRef}
            value={localValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            className={`prompt-textarea ${autoResize && !height ? 'auto-resize' : ''}`}
            style={{
              height: height ? (typeof height === 'number' ? `${height}px` : height) : undefined,
              minHeight: height ? 'unset' : undefined,
              resize: height ? 'none' : undefined
            }}
          />
          
          {/* 字符计数 */}
          {showCharCount && (
            <div className={`char-count ${isOverLimit ? 'over-limit' : ''}`}>
              {charCount}{maxLength > 0 && `/${maxLength}`}
            </div>
          )}
        </div>

        {/* 验证错误信息 */}
        {!isValid && validationMessage && (
          <div className="validation-message error">
            {validationMessage}
          </div>
        )}

        {/* 建议按钮 */}
        {suggestions.length > 0 && (
          <div className="suggestions-section">
            <button
              type="button"
              className="suggestions-toggle"
              onClick={() => setShowSuggestions(!showSuggestions)}
              disabled={disabled}
            >
              <span>{t('aiToolTemplate.promptInput.suggestions.toggle')}</span>
              <svg 
                className={`toggle-icon ${showSuggestions ? 'expanded' : ''}`}
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor"
              >
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>

            {/* 建议列表 */}
            {showSuggestions && (
              <div className="suggestions-list">
                <div className="suggestions-header">
                  <span>{t('aiToolTemplate.promptInput.suggestions.title')}</span>
                </div>
                <div className="suggestions-grid">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                      disabled={disabled}
                    >
                      <div className="suggestion-content">
                        {typeof suggestion === 'object' ? (
                          <>
                            {suggestion.title && (
                              <div className="suggestion-title">{suggestion.title}</div>
                            )}
                            <div className="suggestion-text">
                              {suggestion.content || suggestion.text}
                            </div>
                          </>
                        ) : (
                          <div className="suggestion-text">{suggestion}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptInput;
